import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { Download, FileText, Table, Loader2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { format } from 'date-fns';

interface DashboardStats {
  totalPatients?: number;
  activePatients?: number;
  totalSessions?: number;
  completedSessions?: number;
  recentSessions?: number;
  avgResponseTime?: number;
  redFlagCases?: number;
  totalSupervisors?: number;
  totalAgencies?: number;
  pendingAgencies?: number;
}

interface ChartRef {
  ref: React.RefObject<HTMLDivElement>;
  title: string;
}

interface DashboardExportProps {
  stats: DashboardStats;
  agencyName?: string;
  selectedAgencyName?: string;
  dateRange?: { from?: Date; to?: Date };
  isAllAgencies?: boolean;
  analyticsContainerRef?: React.RefObject<HTMLDivElement>;
  chartRefs?: ChartRef[];
}

export default function DashboardExport({
  stats,
  agencyName,
  selectedAgencyName,
  dateRange,
  isAllAgencies = false,
  analyticsContainerRef,
  chartRefs = [],
}: DashboardExportProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState<'pdf' | 'csv' | null>(null);
  const [selectedCharts, setSelectedCharts] = useState<Record<string, boolean>>({});
  const [showPdfOptions, setShowPdfOptions] = useState(false);

  const storageKey = `dashboard-export-charts-${isAllAgencies ? 'all' : 'agency'}`;

  // Load saved preferences or initialize all charts as selected
  useEffect(() => {
    if (chartRefs.length > 0) {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // Merge saved preferences with available charts (in case charts changed)
          const merged: Record<string, boolean> = {};
          chartRefs.forEach((chart) => {
            merged[chart.title] = parsed[chart.title] ?? true;
          });
          setSelectedCharts(merged);
        } catch {
          // If parsing fails, initialize all as selected
          const initial: Record<string, boolean> = {};
          chartRefs.forEach((chart) => {
            initial[chart.title] = true;
          });
          setSelectedCharts(initial);
        }
      } else {
        const initial: Record<string, boolean> = {};
        chartRefs.forEach((chart) => {
          initial[chart.title] = true;
        });
        setSelectedCharts(initial);
      }
    }
  }, [chartRefs.length, storageKey]);

  // Save preferences to localStorage when they change
  useEffect(() => {
    if (Object.keys(selectedCharts).length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(selectedCharts));
    }
  }, [selectedCharts, storageKey]);

  const toggleChart = (title: string) => {
    setSelectedCharts((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const selectAllCharts = () => {
    const allSelected: Record<string, boolean> = {};
    chartRefs.forEach((chart) => {
      allSelected[chart.title] = true;
    });
    setSelectedCharts(allSelected);
  };

  const deselectAllCharts = () => {
    const noneSelected: Record<string, boolean> = {};
    chartRefs.forEach((chart) => {
      noneSelected[chart.title] = false;
    });
    setSelectedCharts(noneSelected);
  };

  const selectedCount = Object.values(selectedCharts).filter(Boolean).length;
  const allSelected = selectedCount === chartRefs.length;
  const noneSelected = selectedCount === 0;

  const formatDateRange = () => {
    if (dateRange?.from && dateRange?.to) {
      return `${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`;
    }
    return 'All Time';
  };

  const generateFilename = (extension: string) => {
    let scope: string;
    if (selectedAgencyName) {
      scope = selectedAgencyName.replace(/\s+/g, '-').toLowerCase();
    } else if (isAllAgencies) {
      scope = 'all-agencies';
    } else {
      scope = (agencyName || 'agency').replace(/\s+/g, '-').toLowerCase();
    }
    const dateStr = format(new Date(), 'yyyy-MM-dd');
    return `dashboard-${scope}-${dateStr}.${extension}`;
  };

  const exportToPDF = async () => {
    setIsExporting(true);
    setExportType('pdf');
    setShowPdfOptions(false);

    try {
      const doc = new jsPDF({ orientation: 'portrait' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = 20;

      // Header
      doc.setFillColor(34, 139, 34);
      doc.rect(0, 0, pageWidth, 40, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('Dashboard Report', margin, 22);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(formatDateRange(), margin, 32);

      yPos = 50;
      doc.setTextColor(0, 0, 0);

      // Scope
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      if (isAllAgencies) {
        doc.text('Scope: All Agencies', margin, yPos);
      } else if (agencyName) {
        doc.text(`Agency: ${agencyName}`, margin, yPos);
      }
      yPos += 6;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated on ${format(new Date(), "MMMM d, yyyy 'at' h:mm a")}`, margin, yPos);
      yPos += 15;

      // Statistics Section
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Key Statistics', margin, yPos);
      yPos += 8;

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;

      const statsRows: [string, string][] = [];
      
      if (stats.totalAgencies !== undefined) {
        statsRows.push(['Total Agencies', stats.totalAgencies.toLocaleString()]);
      }
      if (stats.pendingAgencies !== undefined) {
        statsRows.push(['Pending Agencies', stats.pendingAgencies.toLocaleString()]);
      }
      if (stats.totalPatients !== undefined) {
        statsRows.push(['Total Patients', stats.totalPatients.toLocaleString()]);
      }
      if (stats.activePatients !== undefined) {
        statsRows.push(['Active Patients', stats.activePatients.toLocaleString()]);
      }
      if (stats.totalSessions !== undefined) {
        statsRows.push(['Total Triage Sessions', stats.totalSessions.toLocaleString()]);
      }
      if (stats.completedSessions !== undefined) {
        statsRows.push(['Completed Sessions', stats.completedSessions.toLocaleString()]);
      }
      if (stats.recentSessions !== undefined) {
        statsRows.push(['Recent Sessions (24h)', stats.recentSessions.toLocaleString()]);
      }
      if (stats.redFlagCases !== undefined) {
        statsRows.push(['Red Flag Cases', stats.redFlagCases.toLocaleString()]);
      }
      if (stats.totalSupervisors !== undefined) {
        statsRows.push(['Total Supervisors', stats.totalSupervisors.toLocaleString()]);
      }

      doc.setFontSize(11);
      statsRows.forEach(([label, value]) => {
        doc.setFont('helvetica', 'normal');
        doc.text(label, margin, yPos);
        doc.setFont('helvetica', 'bold');
        doc.text(value, pageWidth - margin - 40, yPos);
        yPos += 8;
      });

      yPos += 15;

      // Capture selected chart refs
      const chartsToExport = chartRefs.filter((chart) => selectedCharts[chart.title]);
      
      if (chartsToExport.length > 0) {
        for (const chartRef of chartsToExport) {
          if (chartRef.ref.current) {
            doc.addPage();
            let chartYPos = 20;

            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text(chartRef.title, margin, chartYPos);
            chartYPos += 10;

            try {
              const canvas = await html2canvas(chartRef.ref.current, {
                scale: 1.5,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
              });

              const imgData = canvas.toDataURL('image/png');
              const imgWidth = pageWidth - 2 * margin;
              const imgHeight = (canvas.height * imgWidth) / canvas.width;
              const maxHeight = pageHeight - chartYPos - 30;

              const finalHeight = Math.min(imgHeight, maxHeight);
              doc.addImage(imgData, 'PNG', margin, chartYPos, imgWidth, finalHeight);
            } catch (err) {
              console.error(`Error capturing chart ${chartRef.title}:`, err);
              doc.setFontSize(10);
              doc.setTextColor(150, 150, 150);
              doc.text('Chart could not be captured.', margin, chartYPos);
            }
          }
        }
      } else if (analyticsContainerRef?.current) {
        // Fallback to legacy single container capture
        doc.addPage();
        let legacyYPos = 20;

        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Analytics Charts', margin, legacyYPos);
        legacyYPos += 10;

        try {
          const canvas = await html2canvas(analyticsContainerRef.current, {
            scale: 1.5,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
          });

          const imgData = canvas.toDataURL('image/png');
          const imgWidth = pageWidth - 2 * margin;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          let heightLeft = imgHeight;
          let position = legacyYPos;

          doc.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
          heightLeft -= (pageHeight - position - 20);

          while (heightLeft > 0) {
            doc.addPage();
            position = -heightLeft + 20;
            doc.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
            heightLeft -= pageHeight - 40;
          }
        } catch (err) {
          console.error('Error capturing charts:', err);
          doc.setFontSize(10);
          doc.setTextColor(150, 150, 150);
          doc.text('Charts could not be captured.', margin, legacyYPos);
        }
      }

      // Footer
      const totalPages = doc.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        const footerY = doc.internal.pageSize.getHeight() - 12;
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);

        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text('CheckFirst - Dashboard Report', margin, footerY);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 25, footerY);
      }

      doc.save(generateFilename('pdf'));
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    } finally {
      setIsExporting(false);
      setExportType(null);
    }
  };

  const exportToCSV = () => {
    setIsExporting(true);
    setExportType('csv');

    try {
      const rows: string[][] = [
        ['Dashboard Export Report'],
        [`Generated: ${format(new Date(), "MMMM d, yyyy 'at' h:mm a")}`],
        [`Date Range: ${formatDateRange()}`],
        [isAllAgencies ? 'Scope: All Agencies' : `Agency: ${agencyName || 'N/A'}`],
        [],
        ['Metric', 'Value'],
      ];

      if (stats.totalAgencies !== undefined) {
        rows.push(['Total Agencies', stats.totalAgencies.toString()]);
      }
      if (stats.pendingAgencies !== undefined) {
        rows.push(['Pending Agencies', stats.pendingAgencies.toString()]);
      }
      if (stats.totalPatients !== undefined) {
        rows.push(['Total Patients', stats.totalPatients.toString()]);
      }
      if (stats.activePatients !== undefined) {
        rows.push(['Active Patients', stats.activePatients.toString()]);
      }
      if (stats.totalSessions !== undefined) {
        rows.push(['Total Triage Sessions', stats.totalSessions.toString()]);
      }
      if (stats.completedSessions !== undefined) {
        rows.push(['Completed Sessions', stats.completedSessions.toString()]);
      }
      if (stats.recentSessions !== undefined) {
        rows.push(['Recent Sessions (24h)', stats.recentSessions.toString()]);
      }
      if (stats.redFlagCases !== undefined) {
        rows.push(['Red Flag Cases', stats.redFlagCases.toString()]);
      }
      if (stats.totalSupervisors !== undefined) {
        rows.push(['Total Supervisors', stats.totalSupervisors.toString()]);
      }

      const csvContent = rows.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = generateFilename('csv');
      link.click();
      URL.revokeObjectURL(link.href);

      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    } finally {
      setIsExporting(false);
      setExportType(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={isExporting} className="gap-2">
          {isExporting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <Download className="h-4 w-4" />
              Export
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 bg-background border shadow-lg z-50">
        {!showPdfOptions ? (
          <>
            <DropdownMenuItem 
              onClick={() => chartRefs.length > 0 ? setShowPdfOptions(true) : exportToPDF()} 
              disabled={isExporting}
              className="cursor-pointer"
            >
              <FileText className="h-4 w-4 mr-2" />
              Export as PDF
              {chartRefs.length > 0 && <ChevronRight className="h-4 w-4 ml-auto" />}
              {exportType === 'pdf' && <Loader2 className="h-4 w-4 ml-2 animate-spin" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={exportToCSV} disabled={isExporting} className="cursor-pointer">
              <Table className="h-4 w-4 mr-2" />
              Export as CSV
              {exportType === 'csv' && <Loader2 className="h-4 w-4 ml-2 animate-spin" />}
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>Select Charts</span>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={(e) => {
                    e.preventDefault();
                    allSelected ? deselectAllCharts() : selectAllCharts();
                  }}
                >
                  {allSelected ? 'None' : 'All'}
                </Button>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {chartRefs.map((chart) => (
              <DropdownMenuCheckboxItem
                key={chart.title}
                checked={selectedCharts[chart.title] ?? true}
                onCheckedChange={() => toggleChart(chart.title)}
                onSelect={(e) => e.preventDefault()}
              >
                {chart.title}
              </DropdownMenuCheckboxItem>
            ))}
            <DropdownMenuSeparator />
            <div className="p-2 flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex-1"
                onClick={() => setShowPdfOptions(false)}
              >
                Back
              </Button>
              <Button
                size="sm"
                className="flex-1"
                onClick={exportToPDF}
                disabled={isExporting || noneSelected}
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>Export ({selectedCount})</>
                )}
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}